import { type Ref, useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import loadable from '@loadable/component';

import {
    type CartProduct,
    GA4EventName,
    GA4FeatureCategory,
    trackAnalyticsEvent,
    trackEecProductImpression,
} from '@jsmdg/tracking';
import { ButtonVariant, ProductTile, useImpressionCallback } from '@jsmdg/yoshi';
import {
    type ClosestSearchLocation,
    type ExperienceProductInformation,
    type ProductSearchImageItem,
    type ProductSearchLocationItem,
    type ProductSearchPriceItem,
    type Rating,
} from '../../../shared/types';
import { useProduct } from '../../hooks/useProduct';
import { ProductSpecification } from '../../types/enums/productSpecification';
import { trackProductTileView } from './giftBoxRelatedProductWrapper.tracking';

const LoadableProductInfoModal = loadable(
    async () => import('../ProductInfoModal/ProductInfoModal'),
);

const messages = defineMessages({
    a11YCloseServicesMessage: { defaultMessage: 'Leistungsbeschreibung schließen' },
    onlineExperienceLocation: { defaultMessage: 'Online-Erlebnis' },
    multipleLocations: { defaultMessage: 'an {locationCount} Orten' },
});

type GiftBoxRelatedProductWrapperProps = {
    readonly manufacturerSku: string;
    readonly position: number;
    readonly name: string;
    readonly productType: string | null;
    readonly trackingPath: string[];
    readonly classifications: string[] | null;
    readonly rating: Rating;
    readonly isInPreview: boolean;
    readonly lazyLoad: boolean;
    readonly locations?: ProductSearchLocationItem[];
    readonly closestLocation?: ClosestSearchLocation | null;
    readonly price: ProductSearchPriceItem;
    readonly originalPrice?: ProductSearchPriceItem;
    readonly images: ProductSearchImageItem[];
    readonly minParticipants?: number;
    readonly maxParticipants?: number;
    readonly productInformation: ExperienceProductInformation;
};
const GiftBoxRelatedProductWrapper = ({
    classifications = null,
    closestLocation,
    images,
    isInPreview,
    lazyLoad,
    locations,
    manufacturerSku,
    maxParticipants,
    minParticipants,
    name = '',
    originalPrice,
    position,
    price,
    productInformation,
    productType = null,
    rating,
    trackingPath = [],
}: GiftBoxRelatedProductWrapperProps): JSX.Element => {
    const intl = useIntl();
    const { discountBadge } = useProduct();
    const [isProductInfoModalOpen, setIsProductInfoModalOpen] = useState(false);

    const isOnlineExperience =
        classifications?.includes('LocationOnline') &&
        locations?.some(element => element.name === 'Landesweit');

    const hasClosestLocation = !!closestLocation && !isOnlineExperience;

    const getLocationLabel = (): string | undefined => {
        if (!locations || locations?.length === 0) {
            return undefined;
        }

        if (hasClosestLocation) {
            return closestLocation.name;
        }

        if (isOnlineExperience) {
            return intl.formatMessage(messages.onlineExperienceLocation);
        }

        if (locations?.length > 1) {
            return intl.formatMessage(messages.multipleLocations, {
                locationCount: locations.length,
            });
        }

        return locations[0]?.name;
    };

    const trackProductImpression = useCallback(async () => {
        const product = {
            productType,
            title: name,
            trackingPath,
            manufacturerSku,
            price: {
                gross: {
                    amount: price?.grossAmount,
                    currencyCode: price?.currencyCode,
                },
            },
            originalPrice: originalPrice && {
                gross: {
                    amount: originalPrice.grossAmount,
                    currencyCode: originalPrice.currencyCode,
                },
            },
            discountBadgeId: discountBadge?.id,
            discountBadgeName: discountBadge?.text,
        };

        await window.yieldToMainThread();
        trackEecProductImpression({
            product: product as unknown as CartProduct,
            position: position + 1,
            list: 'ProductSelectionBlock',
        });
        await trackProductTileView(name, position + 1);
    }, [
        productType,
        name,
        trackingPath,
        manufacturerSku,
        price,
        originalPrice,
        position,
        discountBadge?.id,
        discountBadge?.text,
    ]);

    const setImpressionTrackedElement = useImpressionCallback(trackProductImpression);

    const openModal = useCallback(() => setIsProductInfoModalOpen(true), []);
    const closeModal = useCallback(() => setIsProductInfoModalOpen(false), []);

    const onDetailsModalOpen = (): void => {
        openModal();
        trackAnalyticsEvent({
            category: 'ProductTile',
            action: 'Open',
            label: 'Open Popup Box PDP',
            eventData: {
                eventName: GA4EventName.ClickProductSelectorBlock,
                feature_category: GA4FeatureCategory.ProductdetailBoxList,
                product_title: name,
                position: position + 1,
            },
        });
    };

    const onDetailsModelClose = (isClosedByBackButton = false): void => {
        closeModal();
        trackAnalyticsEvent({
            category: 'ProductTile',
            action: 'Close',
            label: isClosedByBackButton ? 'Close Back Popup Box PDP' : 'Close X Popup Box PDP',
        });
    };

    return (
        <>
            <ProductTile
                className="border-bottom-0"
                name={name}
                images={images}
                locale={intl.locale}
                closestLocation={hasClosestLocation ? closestLocation : undefined}
                locations={locations}
                attributes={
                    [isInPreview && ProductSpecification.PREVIEW].filter(Boolean) as string[]
                }
                rating={rating}
                addToCartButtonData={{
                    label: 'Details öffnen',
                    onClick: onDetailsModalOpen,
                    variant: ButtonVariant.Outlined,
                    className: 'w-100',
                }}
                lazyLoad={lazyLoad}
                locationLabel={getLocationLabel()}
                ref={setImpressionTrackedElement as Ref<HTMLDivElement> | undefined}
            />
            {isProductInfoModalOpen && (
                <LoadableProductInfoModal
                    isOpen={isProductInfoModalOpen}
                    onRequestClose={onDetailsModelClose}
                    title={name}
                    rating={rating}
                    minParticipants={minParticipants}
                    maxParticipants={maxParticipants}
                    productInformation={productInformation}
                    images={images}
                />
            )}
        </>
    );
};

export { GiftBoxRelatedProductWrapper };
